import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';

// =====================================================
// 1. MOUSE TRAIL EFFECT - Thin Line (Minimal)
// =====================================================
export const MouseTrail = () => {
  const [trail, setTrail] = useState([]);
  const trailLength = 20;
  const animationRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setTrail(prev => {
        const newTrail = [...prev, { x: e.clientX, y: e.clientY, id: Date.now() }];
        if (newTrail.length > trailLength) {
          return newTrail.slice(-trailLength);
        }
        return newTrail;
      });
    };

    // Trail'i yavaşça kısalt (arkadan kaybolma efekti)
    const fadeTrail = () => {
      setTrail(prev => {
        if (prev.length > 0) {
          return prev.slice(1); // İlk (en eski) noktayı kaldır
        }
        return prev;
      });
      animationRef.current = requestAnimationFrame(fadeTrail);
    };

    // Her 50ms'de bir en eski noktayı kaldır
    const intervalId = setInterval(() => {
      setTrail(prev => {
        if (prev.length > 0) {
          return prev.slice(1);
        }
        return prev;
      });
    }, 50);

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearInterval(intervalId);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  if (trail.length < 2) return null;

  // Her segment için ayrı opacity hesapla (eski = şeffaf, yeni = opak)
  const segments = [];
  for (let i = 1; i < trail.length; i++) {
    const opacity = (i / trail.length) * 0.6; // 0'dan 0.6'ya kadar
    segments.push({
      x1: trail[i - 1].x,
      y1: trail[i - 1].y,
      x2: trail[i].x,
      y2: trail[i].y,
      opacity
    });
  }

  return (
    <svg
      className="fixed inset-0 pointer-events-none z-[9998]"
      style={{ width: '100vw', height: '100vh' }}
    >
      {segments.map((seg, i) => (
        <line
          key={i}
          x1={seg.x1}
          y1={seg.y1}
          x2={seg.x2}
          y2={seg.y2}
          stroke={`rgba(56, 189, 248, ${seg.opacity})`}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
};

// =====================================================
// 2. SCROLL PROGRESS BAR
// =====================================================
export const ScrollProgressBar = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollProgress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setProgress(scrollProgress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="fixed top-0 left-0 right-0 h-[2px] bg-transparent z-[9999]">
      <motion.div
        className="h-full bg-gradient-to-r from-[#38BDF8] via-[#A78BFA] to-[#38BDF8]"
        style={{ width: `${progress}%` }}
        initial={{ opacity: 0 }}
        animate={{ opacity: progress > 0 ? 1 : 0 }}
        transition={{ duration: 0.1 }}
      />
    </div>
  );
};

// =====================================================
// 3. PAGE TRANSITION WRAPPER
// =====================================================
export const PageTransition = ({ children }) => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ 
          duration: 0.3, 
          ease: [0.22, 1, 0.36, 1]
        }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

// =====================================================
// 4. BUTTON RIPPLE EFFECT HOOK
// =====================================================
export const useRipple = () => {
  const [ripples, setRipples] = useState([]);

  const addRipple = useCallback((e) => {
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const size = Math.max(rect.width, rect.height) * 2;

    const newRipple = {
      x,
      y,
      size,
      id: Date.now()
    };

    setRipples(prev => [...prev, newRipple]);

    setTimeout(() => {
      setRipples(prev => prev.filter(r => r.id !== newRipple.id));
    }, 600);
  }, []);

  const RippleContainer = () => (
    <span className="absolute inset-0 overflow-hidden rounded-inherit pointer-events-none">
      {ripples.map(ripple => (
        <span
          key={ripple.id}
          className="absolute rounded-full bg-white/30 animate-ripple"
          style={{
            left: ripple.x - ripple.size / 2,
            top: ripple.y - ripple.size / 2,
            width: ripple.size,
            height: ripple.size,
          }}
        />
      ))}
    </span>
  );

  return { addRipple, RippleContainer };
};

// =====================================================
// 5. LOADING SKELETON COMPONENTS
// =====================================================
export const Skeleton = ({ className = '', variant = 'rectangular' }) => {
  const baseClasses = 'animate-pulse bg-gradient-to-r from-white/5 via-white/10 to-white/5 bg-[length:200%_100%]';
  
  const variants = {
    rectangular: 'rounded-lg',
    circular: 'rounded-full',
    text: 'rounded h-4'
  };

  return (
    <div className={`${baseClasses} ${variants[variant]} ${className}`} />
  );
};

export const CardSkeleton = () => (
  <div className="bg-white/5 rounded-2xl p-6 space-y-4">
    <Skeleton className="h-40 w-full" />
    <Skeleton variant="text" className="w-3/4" />
    <Skeleton variant="text" className="w-1/2" />
    <div className="flex gap-2 mt-4">
      <Skeleton className="h-8 w-20 rounded-full" />
      <Skeleton className="h-8 w-20 rounded-full" />
    </div>
  </div>
);

export const PageSkeleton = () => (
  <div className="min-h-screen bg-[#030303] pt-32 px-6">
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="text-center space-y-4">
        <Skeleton className="h-12 w-64 mx-auto" />
        <Skeleton variant="text" className="w-96 mx-auto" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <CardSkeleton key={i} />
        ))}
      </div>
    </div>
  </div>
);

// =====================================================
// 6. ANIMATED COUNTER
// =====================================================
export const AnimatedCounter = ({ 
  value, 
  duration = 2000, 
  prefix = '', 
  suffix = '',
  className = ''
}) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          
          const numericValue = parseFloat(value.toString().replace(/[^0-9.]/g, ''));
          const startTime = Date.now();
          
          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Easing function for smooth animation
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const currentValue = numericValue * easeOutQuart;
            
            setCount(currentValue);
            
            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };
          
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [value, duration, hasAnimated]);

  const formatValue = () => {
    const numStr = value.toString();
    const hasDecimal = numStr.includes('.');
    const isPercentage = numStr.includes('%');
    const hasX = numStr.includes('x');
    
    let formatted = hasDecimal ? count.toFixed(1) : Math.round(count);
    
    if (isPercentage) return `${prefix}${formatted}%`;
    if (hasX) return `${prefix}${formatted}x`;
    return `${prefix}${formatted}${suffix}`;
  };

  return (
    <span ref={ref} className={className}>
      {formatValue()}
    </span>
  );
};

// =====================================================
// 7. ENHANCED GLOW BUTTON
// =====================================================
export const GlowButton = ({ 
  children, 
  onClick, 
  className = '',
  variant = 'primary',
  ...props 
}) => {
  const { addRipple, RippleContainer } = useRipple();

  const variants = {
    primary: 'bg-[#38BDF8] hover:bg-[#38BDF8]/90 text-black',
    secondary: 'bg-white/10 hover:bg-white/20 text-white border border-white/20',
    outline: 'bg-transparent border-2 border-[#38BDF8] text-[#38BDF8] hover:bg-[#38BDF8]/10'
  };

  return (
    <button
      onClick={(e) => {
        addRipple(e);
        onClick?.(e);
      }}
      className={`
        relative overflow-hidden px-6 py-3 rounded-full font-medium
        transition-all duration-300 ease-out
        hover:shadow-[0_0_30px_rgba(56,189,248,0.4)]
        hover:scale-[1.02] active:scale-[0.98]
        ${variants[variant]}
        ${className}
      `}
      {...props}
    >
      <RippleContainer />
      <span className="relative z-10">{children}</span>
    </button>
  );
};

// =====================================================
// 8. INPUT WITH FOCUS ANIMATION
// =====================================================
export const AnimatedInput = ({ 
  label, 
  type = 'text', 
  className = '',
  ...props 
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);

  return (
    <div className={`relative ${className}`}>
      <input
        type={type}
        onFocus={() => setIsFocused(true)}
        onBlur={(e) => {
          setIsFocused(false);
          setHasValue(e.target.value !== '');
        }}
        onChange={(e) => setHasValue(e.target.value !== '')}
        className={`
          w-full bg-white/5 border rounded-xl px-4 py-3 text-white
          transition-all duration-300 outline-none
          ${isFocused 
            ? 'border-[#38BDF8] shadow-[0_0_20px_rgba(56,189,248,0.2)]' 
            : 'border-white/10 hover:border-white/20'}
        `}
        placeholder=" "
        {...props}
      />
      {label && (
        <label
          className={`
            absolute left-4 transition-all duration-300 pointer-events-none
            ${isFocused || hasValue 
              ? 'text-xs -top-2 text-[#38BDF8] bg-[#030303] px-1' 
              : 'text-white/50 top-3'}
          `}
        >
          {label}
        </label>
      )}
      {/* Focus line animation */}
      <div 
        className={`
          absolute bottom-0 left-1/2 h-[2px] bg-[#38BDF8] 
          transition-all duration-300 ease-out
          ${isFocused ? 'w-full -translate-x-1/2' : 'w-0'}
        `}
      />
    </div>
  );
};

// =====================================================
// MAIN GLOBAL EFFECTS COMPONENT
// =====================================================
const GlobalEffects = () => {
  return (
    <>
      <MouseTrail />
      <ScrollProgressBar />
    </>
  );
};

export default GlobalEffects;
